import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { View, StyleSheet, FlatList, StatusBar, RefreshControl, Text } from 'react-native';
import { useAppSelector, useAppDispatch } from '@/store';
import { selectSortedEvents } from '@/store/selectors/residentsEventsSelectors';
import { fetchHomeData } from '@/store/slices/residents/homeSlice';
import { Event as ResioEvent } from '@/types/residents/event';
import { eventStrings } from '@/constants/residents/strings';
import { EVENTS_COLORS } from '@/constants/residents/mobile/eventsColors';

// Layout components
import { BaseResidentMobileScreen } from '@/components/residents/mobile/BaseResidentMobileScreen';
import { MobileScreenLayout } from '@/components/residents/mobile/MobileScreenLayout';

// Events components
import { EventsNavHeader, EventTabType } from '@/components/residents/events/mobile/EventsNavHeader';
import { MobileEventCard } from '@/components/residents/events/mobile/MobileEventCard';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';

// Icons
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';

const NoEventsIcon = RESIDENTS_SVG_COMPONENTS.NO_EVENTS_ICON;

/**
 * Resident Events Screen - Mobile
 * Matches Figma design with tab navigation (Upcoming/Past) and new event card design
 */
export const ResidentEventsScreen: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { isLoading, unreadBulletinsCount } = useAppSelector((state) => state.home);
  const events = useAppSelector(selectSortedEvents);
  
  const [refreshing, setRefreshing] = useState(false);
  const [selectedTab, setSelectedTab] = useState<EventTabType>('upcoming');

  // Load events on mount
  useEffect(() => {
    if (user?.propertyId) {
      dispatch(fetchHomeData(user.propertyId));
    }
  }, [dispatch, user?.propertyId]);

  // Handle tab change
  const handleTabChange = useCallback((tab: EventTabType) => {
    setSelectedTab(tab);
  }, []);

  // Handle refresh
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      if (user?.propertyId) {
        await dispatch(fetchHomeData(user.propertyId)).unwrap();
      }
    } catch (error) {
      console.error('Failed to refresh events:', error);
    } finally {
      setRefreshing(false);
    }
  }, [dispatch, user?.propertyId]);

  // Filter events based on selected tab
  const filteredEvents = useMemo(() => {
    const validEvents = events.filter((event) => event && event.id);
    const now = new Date();
    
    if (selectedTab === 'upcoming') {
      return validEvents.filter((event) => {
        if (!event.startTime) return true; // Show events without dates in upcoming
        return new Date(event.startTime) >= now;
      });
    } else {
      return validEvents.filter((event) => {
        if (!event.startTime) return false;
        return new Date(event.startTime) < now;
      });
    }
  }, [events, selectedTab]);

  // Render empty state
  const renderEmptyState = () => {
    const isUpcoming = selectedTab === 'upcoming';
    const emptyContent = isUpcoming ? eventStrings.empty : eventStrings.emptyPast;
    
    return (
      <View style={styles.emptyState}>
        <NoEventsIcon width={80} height={80} color={EVENTS_COLORS.textMuted} />
        <Text style={styles.emptyStateTitle}>{emptyContent.title}</Text>
        <Text style={styles.emptyStateMessage}>{emptyContent.description}</Text>
      </View>
    );
  };

  // Render event item
  const renderEventItem = ({ item }: { item: ResioEvent }) => (
    <MobileEventCard event={item} isPast={selectedTab === 'past'} />
  );

  return (
    <BaseResidentMobileScreen
      screenName="ResidentEvents"
      darkBackground={true}
    >
      <StatusBar barStyle="light-content" />
      
      <MobileScreenLayout
        backgroundColor={EVENTS_COLORS.background}
        scrollable={false}
        lightStatusBar={false}
      >
        {/* Nav Header with Tabs */}
        <EventsNavHeader
          selectedTab={selectedTab}
          onTabChange={handleTabChange}
        />

        {/* Content */}
        {isLoading && !events.length ? (
          <View style={styles.loadingContainer}>
            <LoadingSpinner message="Loading events..." variant="admin" />
          </View>
        ) : (
          <FlatList
            data={filteredEvents}
            keyExtractor={(item) => `event-${item.id}`}
            renderItem={renderEventItem}
            contentContainerStyle={styles.listContent}
            ListEmptyComponent={renderEmptyState}
            refreshControl={
              <RefreshControl
                refreshing={refreshing}
                onRefresh={handleRefresh}
                tintColor={EVENTS_COLORS.navHeaderBackground}
              />
            }
            showsVerticalScrollIndicator={false}
            ItemSeparatorComponent={() => <View style={styles.separator} />}
          />
        )}
      </MobileScreenLayout>
    </BaseResidentMobileScreen>
  );
};

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 40,
  },
  listContent: {
    paddingTop: 24,
    paddingBottom: 40,
    paddingHorizontal: 20,
    flexGrow: 1,
  },
  separator: {
    height: 16,
  },
  emptyState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 40,
    paddingTop: 60,
    gap: 12,
  },
  emptyStateTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: EVENTS_COLORS.textPrimary,
    textAlign: 'center',
    marginTop: 8,
  },
  emptyStateMessage: {
    fontSize: 14,
    color: EVENTS_COLORS.textMuted,
    textAlign: 'center',
    lineHeight: 20,
  },
});

export default ResidentEventsScreen;
